import { router } from "./screens/router";

import { useHydrateAppStore, useHydrateUserAccount } from "./appStore";
import "./i18n";

import type { Stripe } from "@stripe/stripe-js";
import { RouterProvider } from "react-router";
import { useHydrateChatSchedule } from "./hooks/useChatSchedule";
import { useStartTimer } from "./screens/reader/hooks/useTimer";
import { useWindowScroll } from "./hooks/useScrollVisibility";

// Lazy-load @stripe/stripe-js so it's code-split from main bundle
const loadStripeAsync = async (): Promise<Stripe | null> => {
  const { loadStripe } = await import("@stripe/stripe-js");
  return loadStripe(window.gc.global.stripe_key);
};

let stripePromiseCache: Promise<Stripe | null> | null = null;

export const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromiseCache) {
    // Clear the cache on failure so the next caller (a real Stripe-
    // dependent flow) can retry instead of inheriting a poisoned promise.
    stripePromiseCache = loadStripeAsync().catch((error) => {
      stripePromiseCache = null;
      throw error;
    });
  }
  return stripePromiseCache;
};

// On prod: prime the Stripe SDK eagerly for fraud-detection fingerprinting.
// Swallow the rejection — if Stripe.js is blocked (ad blocker, corporate
// proxy, flaky network) an unhandled rejection only pollutes Sentry
// without helping the user. getStripePromise() will retry when a flow
// that actually needs Stripe is mounted.
if (window.gc.global.stripeOnAllPages) {
  getStripePromise().catch(() => {});
}

function App() {
  useHydrateUserAccount();
  useHydrateChatSchedule();
  useStartTimer();
  useHydrateAppStore();
  useWindowScroll();

  return <RouterProvider router={router} />;
}

export default App;
